import React from 'react';
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { ApercuStatiqueProps } from './props';
import { Definition, Question, Section } from './types';
import { decrireCondition, decrireFichiers, decrireType, libelleQuestion } from './libelles';

// Aperçu statique d'une définition (même non enregistrée) : intro, sections et questions en liste lisible, conditions
// en clair (« affichée seulement si « But du voyage » est égal à Affaires »). Rien n'est évalué ni saisi ici : le
// rendu interactif est le Dialog « Tester le formulaire » (lot 4b).

const chipCondition = (def: Definition, porteur: Section | Question): React.ReactNode =>
  porteur.condition ? (
    <Chip
      size="small"
      variant="outlined"
      color="info"
      label={`affichée seulement si ${decrireCondition(def, porteur.condition)}`}
      sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }}
    />
  ) : null;

const QuestionApercu: React.FC<{ def: Definition; question: Question }> = ({ def, question }) => {
  const q = question;

  // Paragraphe d'information : encart, jamais de réponse
  if (q.type === 'info') {
    return (
      <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
        <Typography variant="body2">{libelleQuestion(q)}</Typography>
        {q.aide && <Typography variant="caption" color="text.secondary" display="block">{q.aide}</Typography>}
        {q.condition && <Box sx={{ mt: 0.5 }}>{chipCondition(def, q)}</Box>}
      </Alert>
    );
  }

  const fichiers = decrireFichiers(q);
  return (
    <Box>
      {/* component="div" : le Chip « obligatoire » est un <div>, interdit dans le <p> par défaut */}
      <Typography variant="body2" component="div">
        • {libelleQuestion(q)}
        <Typography component="span" variant="body2" color="text.secondary"> — {decrireType(q)}</Typography>
        {q.obligatoire && <Chip size="small" label="obligatoire" color="warning" variant="outlined" sx={{ ml: 1, height: 20 }} />}
      </Typography>
      {q.aide && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 2 }}>{q.aide}</Typography>
      )}
      {(q.type === 'choix' || q.type === 'choix_multiple') && (
        <Box sx={{ pl: 2 }}>
          {q.options && q.options.length ? (
            q.options.map((o) => (
              <Typography key={o.valeur} variant="caption" display="block">◦ {o.libelle || o.valeur}</Typography>
            ))
          ) : (
            <Typography variant="caption" color="error" display="block">Aucune option (au moins une attendue)</Typography>
          )}
        </Box>
      )}
      {fichiers && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 2 }}>{fichiers}</Typography>
      )}
      {q.condition && <Box sx={{ pl: 2, mt: 0.5 }}>{chipCondition(def, q)}</Box>}
    </Box>
  );
};

const ApercuStatique: React.FC<ApercuStatiqueProps> = ({ definition }) => {
  const def = definition;
  return (
    <Stack spacing={2}>
      {def.intro && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>{def.intro}</Typography>
      )}

      {!def.sections.length && (
        <Typography variant="body2" color="text.secondary">Aucune section.</Typography>
      )}

      {def.sections.map((s, i) => (
        <Box key={s.id || i}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {s.titre && s.titre.trim() ? s.titre : `Section ${i + 1}`}
            </Typography>
            {chipCondition(def, s)}
          </Stack>
          {s.questions.length ? (
            <Stack spacing={1} sx={{ pl: 1 }}>
              {s.questions.map((q, j) => <QuestionApercu key={q.id || j} def={def} question={q} />)}
            </Stack>
          ) : (
            <Typography variant="caption" color="error" sx={{ pl: 1 }}>
              Aucune question (une section doit en contenir au moins une)
            </Typography>
          )}
          {i < def.sections.length - 1 && <Divider sx={{ mt: 2 }} />}
        </Box>
      ))}

      {def.message_fin && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>{def.message_fin}</Typography>
      )}
    </Stack>
  );
};

export default ApercuStatique;
